import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { lottoService } from '../services/LottoService';
import { LottoResult, UserChoice } from '../types';
import axios from 'axios';

interface LottoContextType {
  // 상태
  currentSequenceNo: number | null;
  lastSequenceNo: number | null;
  currentPrize: LottoResult | null;
  selectedNumbers: number[];
  maxSelectableNumbers: number;
  userChoices: UserChoice[];
  isLoading: boolean;
  error: string;
  
  // 함수
  selectNumber: (number: number) => void;
  clearSelectedNumbers: () => void;
  saveSelectedNumbers: () => Promise<void>;
  fetchCurrentPrize: (signal?: AbortSignal) => Promise<void>;
  fetchPrizeBySequenceNo: (sequenceNo: number, signal?: AbortSignal) => Promise<number | null>;
  fetchUserChoices: (seqNo?: number, signal?: AbortSignal) => Promise<void>;
  deleteUserChoice: (choiceNo: string) => Promise<void>;
}

const LottoContext = createContext<LottoContextType | undefined>(undefined);

export const useLotto = () => {
  const context = useContext(LottoContext);
  if (!context) {
    throw new Error('useLotto must be used within a LottoProvider');
  }
  return context;
};

interface LottoProviderProps {
  children: ReactNode;
}

export const LottoProvider: React.FC<LottoProviderProps> = ({ children }) => {
  const { guestToken, isAuthenticated, user } = useAuth();
  const [lastSequenceNo, setLastSequenceNo] = useState<number | null>(null);
  const [currentSequenceNo, setCurrentSequenceNo] = useState<number | null>(null);
  const [currentPrize, setCurrentPrize] = useState<LottoResult | null>(null);
  const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
  const [userChoices, setUserChoices] = useState<UserChoice[]>([]);
  const [maxSelectableNumbers] = useState(6);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // 현재 회차 로또 정보 조회
  const fetchCurrentPrize = useCallback(async (signal?: AbortSignal) => {
    if (!guestToken) return;
    
    setIsLoading(true);
    setError('');

    try {
        const prizeData = await lottoService.getThisWeekPrize(guestToken, signal);
        const sequenceNo = prizeData.result.sequenceNo;
        
        setLastSequenceNo(sequenceNo);
        setCurrentSequenceNo(sequenceNo - 1);
        setCurrentPrize(prizeData);
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('현재 회차 로또 정보 조회 실패:', error);
        setError('로또 정보를 불러오는데 실패했습니다.');
    } finally {
        setIsLoading(false);
    }
  }, [guestToken]);

  // 특정 회차 로또 정보 조회
  const fetchPrizeBySequenceNo = useCallback(async (sequenceNo: number, signal?: AbortSignal) => {
    if (!guestToken) return null;
    
    setError('');
    
    try {
      setIsLoading(true);
      
      // 회차 번호 유효성 검사
      if (!sequenceNo || sequenceNo <= 0 || isNaN(sequenceNo)) {
        setIsLoading(false);
        throw new Error('유효하지 않은 회차 번호입니다.');
      }
      
      // 현재 최신 회차보다 큰 회차는 요청하지 않음
      if (lastSequenceNo && sequenceNo >= lastSequenceNo) {
        setIsLoading(false);
        throw new Error(`아직 진행되지 않은 회차입니다. 현재 최신 회차는 ${lastSequenceNo}회 입니다.`);
      }
      
      const prizeData = await lottoService.getPrizeBySequenceNo(guestToken, sequenceNo, signal);
      
      setCurrentPrize(prizeData);
      setCurrentSequenceNo(sequenceNo);
      setIsLoading(false);
      return sequenceNo;
    } catch (error) {
      if (axios.isCancel(error)) {
        setIsLoading(false);
        return null;
      }
      console.error('특정 회차 로또 정보 조회 실패:', error);
      setIsLoading(false);
      
      if (error instanceof Error) {
        if (error.message.includes('범위')) {
          setError(`유효하지 않은 회차입니다. 현재 최신 회차는 ${lastSequenceNo}회 입니다.`);
        } else {
          setError(error.message);
        }
      } else {
        setError('로또 정보를 불러오는데 실패했습니다.');
      }
      
      return null;
    }
  }, [guestToken, lastSequenceNo]);

  // 사용자 선택 번호 조회
  const fetchUserChoices = useCallback(async (seqNo?: number, signal?: AbortSignal) => {
    if (!isAuthenticated || !user) return;
    
    try {
      setIsLoading(true);
      setError('');
      
      const choices = await lottoService.getUserChoices(user.token, seqNo, signal);
      
      setUserChoices(choices);
      setIsLoading(false);
    } catch (error) {
      if (axios.isCancel(error)) {
        setIsLoading(false);
        return;
      }
      // 사용자 선택 번호 조회 실패 - 저장된 번호 로드 문제 진단을 위해 로그 유지
      console.error('사용자 선택 번호 조회 실패:', error);
      setIsLoading(false);
      setError('선택 번호를 불러오는데 실패했습니다.');
    }
  }, [isAuthenticated, user]);

  // 번호 선택
  const selectNumber = useCallback((number: number) => {
    setSelectedNumbers(prev => {
      // 이미 선택된 번호인 경우 제거
      if (prev.includes(number)) {
        return prev.filter(n => n !== number);
      }
      
      // 최대 선택 가능 개수를 초과하는 경우
      if (prev.length >= maxSelectableNumbers) {
        return prev;
      }
      
      // 번호 추가 (오름차순 정렬)
      return [...prev, number].sort((a, b) => a - b);
    });
  }, [maxSelectableNumbers]);

  // 선택 번호 초기화
  const clearSelectedNumbers = useCallback(() => {
    setSelectedNumbers([]);
  }, []);

  // 선택 번호 저장
  const saveSelectedNumbers = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setError('로그인이 필요합니다.');
      return;
    }
    
    if (selectedNumbers.length !== maxSelectableNumbers) {
      setError(`${maxSelectableNumbers}개의 번호를 선택해야 합니다.`);
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      await lottoService.saveUserChoice(user.token, selectedNumbers);
      
      // 저장 후 선택 번호 초기화 및 목록 새로고침
      setSelectedNumbers([]);
      await fetchUserChoices(currentSequenceNo || undefined);
      
      setIsLoading(false);
    } catch (error) {
      // 선택 번호 저장 실패 - 번호 저장 문제 진단을 위해 로그 유지
      console.error('선택 번호 저장 실패:', error);
      setIsLoading(false);
      setError('번호 저장에 실패했습니다.');
    }
  }, [isAuthenticated, user, selectedNumbers, maxSelectableNumbers, fetchUserChoices, currentSequenceNo]);

  // 선택 번호 삭제
  const deleteUserChoice = useCallback(async (choiceNo: string) => {
    if (!isAuthenticated || !user) {
      setError('로그인이 필요합니다.');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      
      await lottoService.deleteUserChoice(user.token, choiceNo);
      
      // 삭제 후 목록 새로고침
      await fetchUserChoices(currentSequenceNo || undefined);
      
      setIsLoading(false);
    } catch (error) {
      // 선택 번호 삭제 실패 - 번호 삭제 문제 진단을 위해 로그 유지
      console.error('선택 번호 삭제 실패:', error);
      setIsLoading(false);
      setError('번호 삭제에 실패했습니다.');
    }
  }, [isAuthenticated, user, fetchUserChoices, currentSequenceNo]);

  // 초기 데이터 로드
  useEffect(() => {
    if (guestToken) {
      fetchCurrentPrize();
    }
  }, [guestToken, fetchCurrentPrize]);

  // 인증 상태 변경 시 사용자 선택 번호 로드
  /*useEffect(() => {
    if (isAuthenticated && user && currentSequenceNo) {
      fetchUserChoices(currentSequenceNo);
    }
  }, [isAuthenticated, user, currentSequenceNo, fetchUserChoices]);
  */

  // 컨텍스트 값 메모이제이션
  const contextValue = useMemo(() => ({
    currentSequenceNo,
    lastSequenceNo,
    currentPrize,
    selectedNumbers,
    maxSelectableNumbers,
    userChoices,
    isLoading,
    error,
    selectNumber,
    clearSelectedNumbers,
    saveSelectedNumbers,
    fetchCurrentPrize,
    fetchPrizeBySequenceNo,
    fetchUserChoices,
    deleteUserChoice
  }), [
    currentSequenceNo,
    lastSequenceNo,
    currentPrize,
    selectedNumbers,
    maxSelectableNumbers,
    userChoices,
    isLoading,
    error,
    selectNumber,
    clearSelectedNumbers,
    saveSelectedNumbers,
    fetchCurrentPrize,
    fetchPrizeBySequenceNo,
    fetchUserChoices,
    deleteUserChoice
  ]);

  return (
    <LottoContext.Provider value={contextValue}>
      {children}
    </LottoContext.Provider>
  );
};

export default LottoContext; 